/**
 * Générateur — Il résout algébriquement différents types d'équations :
 * 1er degré, équations produits, équations de la forme x² = a.
 * Attendu ID : 211 | Niveau : 3ème | Domaine : Calcul littéral (d12, O11)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A8S1-A8S2). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « tester »      — tester une solution (les deux membres évalués), y
 *                     compris avec des racines carrées (jonction O3)
 *   « premier »     — 1er degré simple (rampe : x + b = c, ax = b, b − x = c…)
 *   « deuxmembres » — ax + b = cx + d
 *   « developper »  — avec développement préalable (±( ) et k( ))
 *   « degeneres »   — 0 solution / tous les nombres (QCM calculé, les pépites)
 *   « produit »     — équation produit (facteur numérique piège, racine double)
 *   « carre »       — x² = a quand les solutions se TAPENT (entier/décimal/fraction)
 *   « carreqcm »    — x² = a : nature des solutions (√a intapable → QCM)
 *   « synthese »    — factoriser POUR résoudre (O9 → O11)
 *
 * Adaptations : 3/x = −8 écarté (inconnue au dénominateur, hors canon — le doc
 * le signale) ; solutions en ordre CROISSANT imposé (deux trous sinon
 * échangeables) ; fractions irréductibles, entier dans un trou unique.
 */

import {
    randomChoice, randInt, tex, racineTex,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { rat, ratCmp, fmtAffine, fmtBinome, solveAffine, carre } from './litteral';

const ATTENDU_ID = 211;

const L_TESTER = 'Tester si un nombre est solution';
const L_PREMIER = 'Résoudre une équation simple';
const L_DEUXMEMBRES = 'Résoudre ax + b = cx + d';
const L_DEVELOPPER = 'Résoudre après développement';
const L_DEGENERES = 'Reconnaître les équations sans solution ou toujours vraies';
const L_PRODUIT = 'Résoudre une équation produit';
const L_CARRE = 'Résoudre x² = a (valeurs qui se tapent)';
const L_CARREQCM = 'Discuter le nombre de solutions de x² = a';
const L_SYNTHESE = 'Factoriser pour résoudre';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const coefNonNul = (lo, hi) => { let c = 0; while (c === 0) c = randInt(lo, hi); return c; };

// Bloc de solution en KaTeX : entier → un trou, fraction → une fraction dessinée
// dont numérateur et dénominateur sont deux trous (triple accolade, §0.5bis).
const solTpl = (r) => (r.q === 1 ? `{{${r.p}}}` : `\\dfrac{{{${r.p}}}}{{{${r.q}}}}`);

// Contrainte d'écriture de la réponse (FORMATS_TANOED §0.1) : une phrase, pas
// une parenthèse, et adaptée à la forme réellement attendue.
const consigneSolution = (r) => (r.q === 1
    ? ' Écris la solution avec son signe.'
    : ' Écris le numérateur avec son signe, puis le dénominateur.');

// ═════════ « tester » — les deux membres évalués ═════════

const genTesterTrous = () => {
    if (randInt(0, 1)) {
        // Linéaire : v est-il solution de ax + b = cx + d ?
        const v = coefNonNul(-5, 8);
        const a = coefNonNul(-6, 6); const b = coefNonNul(-9, 9); const c = coefNonNul(-6, 6);
        const gauche = a * v + b;
        // Une fois sur deux, on force l'égalité (d ajusté), sinon d aléatoire.
        const d = randInt(0, 1) ? gauche - c * v : gauche - c * v + coefNonNul(-5, 5);
        const droite = c * v + d;
        const phrase = `$\\text{Membre de gauche : } {{${gauche}}} \\qquad \\text{Membre de droite : } {{${droite}}}$`;
        const payload = makeTexteTrou(phrase,
            `<p>${v} est-il solution de l'équation ${tex(`${fmtAffine(a, b)} = ${fmtAffine(c, d)}`)} ?</p>`
            + `<p><b>Calcule chaque membre pour x = ${v}</b>, puis conclus de tête. `
            + 'Écris chaque résultat avec son signe.</p>',
            { ...opts(L_TESTER), mode: 'saisie' });
        payload._meta.params = { famille: 'lineaire', v, gauche, droite };
        return payload;
    }
    // Racine : ±√s est-il solution de x² − c = d ? (x² vaut s)
    const s = randomChoice([2, 3, 5, 6, 7, 10, 11, 13, 15, 17, 19, 21, 22, 26]);
    const c = randInt(1, 20);
    const gauche = s - c;
    const d = randInt(0, 1) ? gauche : gauche + coefNonNul(-4, 4);
    const negatif = randInt(0, 1) === 1;
    const racine = `${negatif ? '-' : ''}\\sqrt{${s}}`;
    const phrase = `$\\text{Pour } x = ${racine} \\text{, } x^{2} \\text{ vaut } {{${s}}}`
        + ` \\qquad \\text{le membre de gauche vaut } {{${gauche}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${tex(racine)} est-il solution de l'équation ${tex(`x^{2} - ${c} = ${d}`)} ?</p>`
        + '<p><b>Complète, puis conclus de tête.</b></p>',
        { ...opts(L_TESTER), mode: 'saisie' });
    payload._meta.params = { famille: 'racine', s, c, d, gauche, negatif };
    return payload;
};

// ═════════ « premier » — la rampe ═════════

const genPremierTrous = () => {
    let intro; let r = { p: 0, q: 1 }; let forme;
    for (let essai = 0; essai < 40 && r.p === 0; essai++) {
        forme = randomChoice(['plus', 'fois', 'moinsx', 'sur']);
        if (forme === 'plus') {
            const b = coefNonNul(-15, 15); const c = coefNonNul(-15, 15);
            intro = `x ${b < 0 ? '-' : '+'} ${Math.abs(b)} = ${c}`;
            r = rat(c - b, 1);
        } else if (forme === 'fois') {
            const a = coefNonNul(-9, 9); const b = coefNonNul(-15, 15);
            intro = `${a}x = ${b}`;
            r = rat(b, a);
        } else if (forme === 'moinsx') {
            const b = randInt(1, 15); const c = coefNonNul(-15, 15);
            intro = `${b} - x = ${c}`;
            r = rat(b - c, 1);
        } else {
            const a = randInt(2, 9); const b = coefNonNul(-9, 9);
            intro = `\\dfrac{x}{${a}} = ${b}`;
            r = rat(a * b, 1);
        }
    }
    if (r.p === 0) { intro = 'x + 3 = 8'; r = rat(5, 1); forme = 'plus'; } // repli (doc Ex.1)
    const phrase = `$x = ${solTpl(r)}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Résous l'équation ${tex(intro)}.</b>${consigneSolution(r)}</p>`,
        { ...opts(L_PREMIER), mode: 'saisie' });
    payload._meta.params = { forme, r };
    return payload;
};

// ═════════ « deuxmembres » — ax + b = cx + d ═════════

const genDeuxMembresTrous = () => {
    let a; let b; let c; let d; let r = null;
    for (let essai = 0; essai < 60; essai++) {
        a = coefNonNul(-9, 9); b = coefNonNul(-12, 12); c = coefNonNul(-9, 9); d = coefNonNul(-12, 12);
        r = solveAffine(a, b, c, d);
        if (r && r.p !== 0 && Math.abs(r.p) <= 40 && r.q <= 20) break;
        r = null;
    }
    if (!r) { a = 7; b = -4; c = 5; d = 6; r = rat(5, 1); } // repli (doc Ex.3)
    const phrase = `$x = ${solTpl(r)}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Résous l'équation ${tex(`${fmtAffine(a, b)} = ${fmtAffine(c, d)}`)}.</b>${consigneSolution(r)}</p>`,
        { ...opts(L_DEUXMEMBRES), mode: 'saisie' });
    payload._meta.params = { a, b, c, d, r };
    return payload;
};

// ═════════ « developper » — avec développement préalable ═════════

const genDevelopperTrous = () => {
    // c1 − (a1x + b1) = k(a2x + b2)  OU  k(a1x + b1) = c1 + (a2x + b2)
    let intro; let r = null; let params;
    for (let essai = 0; essai < 80; essai++) {
        const forme = randomChoice(['moinsParen', 'kParen']);
        if (forme === 'moinsParen') {
            const c1 = randInt(1, 12); const a1 = coefNonNul(2, 6); const b1 = coefNonNul(-9, 9);
            const k = coefNonNul(2, 5); const a2 = coefNonNul(-5, 5); const b2 = coefNonNul(-9, 9);
            r = solveAffine(-a1, c1 - b1, k * a2, k * b2);
            intro = `${c1} - ${fmtBinome(a1, b1)} = ${k}${fmtBinome(a2, b2)}`;
            params = { forme, c1, a1, b1, k, a2, b2 };
        } else {
            const k = coefNonNul(2, 5); const a1 = coefNonNul(-5, 5); const b1 = coefNonNul(-9, 9);
            const c1 = randInt(1, 12); const a2 = coefNonNul(-5, 5); const b2 = coefNonNul(-9, 9);
            r = solveAffine(k * a1, k * b1, a2, c1 + b2);
            intro = `${k}${fmtBinome(a1, b1)} = ${c1} + ${fmtBinome(a2, b2)}`;
            params = { forme, k, a1, b1, c1, a2, b2 };
        }
        if (r && r.p !== 0 && Math.abs(r.p) <= 40 && r.q <= 15) break;
        r = null;
    }
    if (!r) { intro = '4 - \\left(3x + 1\\right) = 3\\left(x + 5\\right)'; r = rat(-2, 1); params = { repli: true }; } // repli (doc Ex.6)
    const phrase = `$x = ${solTpl(r)}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Résous l'équation ${tex(intro)}.</b> Développe, réduis, puis `
        + `résous.${consigneSolution(r)}</p>`,
        { ...opts(L_DEVELOPPER), mode: 'saisie' });
    payload._meta.params = { ...params, r };
    return payload;
};

// ═════════ « degeneres » — 0 / 1 / infinité de solutions (QCM) ═════════

const CH_UNE = 'Une seule solution';
const CH_AUCUNE = 'Aucune solution';
const CH_TOUS = 'Tous les nombres sont solutions';

const genDegeneresQcm = () => {
    const type = randomChoice(['une', 'aucune', 'tous']);
    const p = coefNonNul(2, 7); const q = randInt(2, 6); const s = randInt(1, 4); const rr = randInt(1, 9);
    const pente = p - q * s;                              // pente du membre de gauche réduit
    const cst = q * rr;
    const gauche = `${p}x + ${q}\\left(${rr} - ${s}x\\right)`;
    let droite;
    if (type === 'tous') droite = fmtAffine(pente, cst);
    else if (type === 'aucune') droite = fmtAffine(pente, cst + coefNonNul(-6, 6));
    else {
        let m;
        do { m = coefNonNul(-7, 7); } while (m === pente);
        droite = fmtAffine(m, randInt(-9, 9));
    }
    const correct = type === 'une' ? CH_UNE : (type === 'aucune' ? CH_AUCUNE : CH_TOUS);
    const payload = makeQcm([CH_UNE, CH_AUCUNE, CH_TOUS], correct,
        `<p>On considère l'équation ${tex(`${gauche} = ${droite}`)}.</p>`
        + '<p>Réduis le membre de gauche, puis compare les deux membres. '
        + '<b>Combien l\'équation a-t-elle de solutions ?</b></p>',
        opts(L_DEGENERES));
    payload._meta.params = { type, gauche, droite };
    return payload;
};

// ═════════ « produit » — équation produit ═════════

const genProduitTrous = () => {
    const variante = randomChoice(['standard', 'facteurk', 'double']);
    if (variante === 'double') {
        // (c − x)(x − c) = 0 → solution UNIQUE x = c (le piège de la racine
        // double). c > 0 : pas de parenthèses imbriquées dans l'affichage.
        const c = randInt(2, 9);
        const phrase = `$\\text{Une seule solution : } x = {{${c}}}$`;
        const payload = makeTexteTrou(phrase,
            `<p><b>Résous ${tex(`\\left(${c} - x\\right)\\left(x - ${c}\\right) = 0`)}.</b> `
            + 'Un produit est nul si et seulement si l\'un de ses facteurs est nul.</p>',
            { ...opts(L_PRODUIT), mode: 'saisie' });
        payload._meta.params = { variante, c };
        return payload;
    }
    let a1; let b1; let a2; let b2; let r1; let r2;
    for (let essai = 0; essai < 80; essai++) {
        a1 = coefNonNul(-6, 8); b1 = coefNonNul(-9, 9);
        a2 = coefNonNul(-6, 8); b2 = coefNonNul(-9, 9);
        r1 = rat(-b1, a1); r2 = rat(-b2, a2);
        if (ratCmp(r1, r2) !== 0) break;
        r1 = null;
    }
    if (!r1) { a1 = 1; b1 = 1; a2 = 1; b2 = -8; r1 = rat(-1, 1); r2 = rat(8, 1); } // repli (doc Ex.10)
    if (ratCmp(r1, r2) > 0) { [r1, r2] = [r2, r1]; }      // ordre croissant imposé
    const prefixe = variante === 'facteurk' ? `${randInt(2, 9)}` : '';
    const phrase = `$x_{1} = ${solTpl(r1)} \\qquad x_{2} = ${solTpl(r2)}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Résous ${tex(`${prefixe}${fmtBinome(a1, b1)}${fmtBinome(a2, b2)} = 0`)}.</b> `
        + 'Un produit est nul si et seulement si l\'un de ses facteurs est nul. '
        + 'Donne les deux solutions <b>dans l\'ordre croissant</b>. '
        + 'Pour une fraction, écris le numérateur avec son signe, puis le dénominateur.</p>',
        { ...opts(L_PRODUIT), mode: 'saisie' });
    payload._meta.params = { variante, a1, b1, a2, b2, r1, r2 };
    return payload;
};

// ═════════ « carre » — x² = a (solutions qui se tapent) ═════════

const genCarreTrous = () => {
    const famille = randomChoice(['entier', 'decimal', 'fraction', 'habille']);
    let intro; let vTpl; let vNegTpl; let params;
    if (famille === 'entier' || famille === 'habille') {
        const k = randInt(2, 13);
        const a = k * k;
        if (famille === 'entier') intro = `${carre()} = ${a}`;
        else {
            const c = randInt(1, 20);
            intro = randInt(0, 1) ? `${carre()} + ${c} = ${a + c}` : `${carre()} - ${c} = ${a - c}`;
        }
        vTpl = `{{${k}}}`; vNegTpl = `{{${-k}}}`;
        params = { famille, k, a };
    } else if (famille === 'decimal') {
        const k10 = randomChoice([15, 25, 35, 45, 55, 65, 75, 85]);   // k = k10/10 (finit par 5 → k² finit par ,25)
        const a100 = k10 * k10;                                        // a = a100/100
        const aAff = `${Math.floor(a100 / 100)},25`;
        const kAff = `${Math.floor(k10 / 10)},5`;
        intro = `${carre()} = ${aAff.replace(',', '{,}')}`;
        vTpl = `{{${kAff}}}`; vNegTpl = `{{-${kAff}}}`;
        params = { famille, k10 };
    } else {
        let p = 5; let q = 4;
        for (let essai = 0; essai < 40; essai++) {
            const pp = randInt(2, 9); const qq = randInt(2, 9);
            if (pp !== qq && rat(pp, qq).p === pp) { p = pp; q = qq; break; }
        }
        intro = `${carre()} = \\dfrac{${p * p}}{${q * q}}`;
        vTpl = `\\dfrac{{{${p}}}}{{{${q}}}}`; vNegTpl = `\\dfrac{{{${-p}}}}{{{${q}}}}`;
        params = { famille, p, q };
    }
    const phrase = `$x = ${vTpl} \\quad\\text{ou}\\quad x = ${vNegTpl}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Résous l'équation ${tex(intro)}.</b> Donne la solution POSITIVE d'abord, `
        + 'puis la négative, avec son signe.</p>',
        { ...opts(L_CARRE), mode: 'saisie' });
    payload._meta.params = params;
    return payload;
};

// ═════════ « carreqcm » — nature des solutions ═════════

const genCarreQcm = () => {
    const cas = randomChoice(['negatif', 'irrationnel', 'positif_carre']);
    let a;
    if (cas === 'negatif') a = -randInt(2, 30);
    else if (cas === 'irrationnel') a = randomChoice([2, 3, 5, 7, 11, 15, 20, 30]);
    else { const k = randInt(2, 12); a = k * k; }
    const chDeux = `Deux solutions : ${racineTex(Math.abs(a))} et ${tex(`-\\sqrt{${Math.abs(a)}}`)}`;
    const chAucune = 'Aucune solution';
    const chUne = `Une seule solution : ${tex(`\\dfrac{${a}}{2}`)}`;
    const correct = cas === 'negatif' ? chAucune : chDeux;
    const payload = makeQcm([chDeux, chAucune, chUne], correct,
        `<p>On considère l'équation ${tex(`${carre()} = ${a}`)}.</p>`
        + '<p><b>Que peut-on dire de ses solutions ?</b></p>',
        opts(L_CARREQCM));
    payload._meta.params = { cas, a };
    return payload;
};

// ═════════ « synthese » — factoriser pour résoudre ═════════

const genSyntheseTrous = () => {
    let p; let q; let f1a; let f1b; let f2a; let f2b; let signe; let A; let B; let r1; let r2;
    for (let essai = 0; essai < 80; essai++) {
        p = coefNonNul(2, 7); q = coefNonNul(-9, 9);
        f1a = coefNonNul(-6, 6); f1b = coefNonNul(-9, 9);
        f2a = coefNonNul(-6, 6); f2b = coefNonNul(-9, 9);
        signe = randomChoice(['+', '-']);
        A = signe === '+' ? f1a + f2a : f1a - f2a;
        B = signe === '+' ? f1b + f2b : f1b - f2b;
        if (A === 0 || B === 0) continue;
        r1 = rat(-q, p); r2 = rat(-B, A);
        if (ratCmp(r1, r2) !== 0) break;
        r1 = null;
    }
    if (!r1) { p = 7; q = -2; f1a = -3; f1b = 2; f2a = 4; f2b = 3; signe = '+'; A = 1; B = 5; r1 = rat(2, 7); r2 = rat(-5, 1); } // repli (doc Ex.13)
    const E = fmtBinome(p, q);
    const gauche = `${E}${fmtBinome(f1a, f1b)} ${signe === '+' ? '+' : '-'} ${E}${fmtBinome(f2a, f2b)}`;
    let s1 = r1; let s2 = r2;
    if (ratCmp(s1, s2) > 0) { [s1, s2] = [s2, s1]; }
    const phrase = `$\\text{Factorisation : } ${E}\\left({{${A}}}x + {{${B}}}\\right)`
        + ` \\qquad x_{1} = ${solTpl(s1)} \\qquad x_{2} = ${solTpl(s2)}$`;
    const payload = makeTexteTrou(phrase,
        `<p><b>Résous ${tex(`${gauche} = 0`)}.</b></p>`
        + `<p>Factorise d'abord par ${tex(E)}, puis donne les deux solutions `
        + '<b>dans l\'ordre croissant</b>. Écris chaque coefficient avec son signe ; '
        + 'pour une fraction, le numérateur signé puis le dénominateur.</p>',
        { ...opts(L_SYNTHESE), mode: 'saisie' });
    payload._meta.params = { p, q, f1a, f1b, f2a, f2b, signe, A, B };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    tester_texte_trou_saisie: genTesterTrous,
    premier_texte_trou_saisie: genPremierTrous,
    deuxmembres_texte_trou_saisie: genDeuxMembresTrous,
    developper_texte_trou_saisie: genDevelopperTrous,
    degeneres_qcm_unique: genDegeneresQcm,
    produit_texte_trou_saisie: genProduitTrous,
    carre_texte_trou_saisie: genCarreTrous,
    carreqcm_qcm_unique: genCarreQcm,
    synthese_texte_trou_saisie: genSyntheseTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'tester', label: L_TESTER, etapes: 2, formatsList: buildFormats('tester') },
    { id: 'premier', label: L_PREMIER, etapes: 1, formatsList: buildFormats('premier') },
    { id: 'deuxmembres', label: L_DEUXMEMBRES, etapes: 3, formatsList: buildFormats('deuxmembres') },
    { id: 'developper', label: L_DEVELOPPER, etapes: 4, formatsList: buildFormats('developper') },
    { id: 'degeneres', label: L_DEGENERES, etapes: 3, formatsList: buildFormats('degeneres') },
    { id: 'produit', label: L_PRODUIT, etapes: 3, formatsList: buildFormats('produit') },
    { id: 'carre', label: L_CARRE, etapes: 2, formatsList: buildFormats('carre') },
    { id: 'carreqcm', label: L_CARREQCM, etapes: 1, formatsList: buildFormats('carreqcm') },
    { id: 'synthese', label: L_SYNTHESE, etapes: 4, formatsList: buildFormats('synthese') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
